import getObjectPropertiesPaths from 'utils/getObjectPropertiesPaths/getObjectPropertiesPaths';
import dotProp from 'dot-prop';
import { useCallback, useEffect, useState } from 'react';
import { clearCaseError } from 'modules/cm/store/useCases';
import { useDispatch } from 'react-redux';
import { CaseError } from 'modules/cm/domain/Case/Error';
import { Nullable } from 'utils/types';
import useReduxCaseStore from 'modules/cm/hooks/useCurrentCaseStore';
import useCasePepperError from 'modules/cm/infrastructure/Pepper/store/useError';

export type ErrorPath = object | undefined;

export type GetErrorsPathsFunction = (fieldName: string) => string[];
type ClearErrorsForFieldsFunction = (fieldNames: string | string[]) => void;

const isString = (val: string | undefined): val is string => {
  return val !== undefined;
};

const isCaseError = (caseError: Nullable<CaseError>): caseError is CaseError => {
  return caseError !== null;
};

interface UseFieldError {
  errorsPaths: ErrorPath[];
  getErrors: GetErrorsPathsFunction;
  clearErrors: ClearErrorsForFieldsFunction;
}

const useFieldError = (module?: keyof CaseError, index?: number | string): UseFieldError => {
  const caseErrors: Nullable<CaseError> = useReduxCaseStore().caseErrors;
  const pepperErrors: Nullable<CaseError> = useCasePepperError();
  const errorsCollection = [caseErrors, pepperErrors].filter(isCaseError);
  const [errorPaths, setErrorPaths] = useState<ErrorPath[]>([]);
  const dispatch = useDispatch();

  const getErrorPaths = useCallback(
    (error: CaseError) => {
      const errors = module ? dotProp.get<any>(error, index !== undefined ? `${module}.${index}` : module) : error;

      const errorPaths = Object.fromEntries(
        getObjectPropertiesPaths(JSON.parse(JSON.stringify({ ...errors })))
          .filter((item) => {
            return dotProp.get(errors, item);
          })
          .map((path) => [path, [dotProp.get(errors, path)]]),
      );

      return errorPaths;
    },
    [index, module],
  );

  useEffect(() => {
    const paths = [pepperErrors, caseErrors].map((caseError) => {
      return caseError ? getErrorPaths(caseError) : [];
    });
    setErrorPaths(paths);
  }, [caseErrors, pepperErrors, getErrorPaths, index, module]);

  const getErrors = (fieldName: string): string[] => {
    return errorPaths.map((errorPath: any) => errorPath[fieldName]).filter(isString);
  };

  const clearErrors = (fieldName: string | string[]): void => {
    const fieldNames = Array.isArray(fieldName) ? fieldName : [fieldName];
    const prefix = index !== undefined ? `${module}.${index}` : module;
    errorsCollection.forEach(async (collection) => {
      const fieldPaths = fieldNames
        .map((fieldName) => {
          return `${prefix}.${fieldName}`;
        })
        .filter((fieldPath) => {
          return collection && dotProp.has(collection, fieldPath);
        });

      await dispatch(clearCaseError(fieldPaths));
    });
  };

  return { errorsPaths: errorPaths, getErrors, clearErrors };
};

export default useFieldError;
